import { DatePicker, Form, Select, Spin } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import moment, { Moment } from 'moment';
import React from 'react';
import { AbsencePeriod, AbsenceReason } from '../../../../core-module/services/absence/types';
import { getAbsenceReasonDisplayName, isBeforeAllowedAbsenceDate } from './helpers';
import { AbsencePeriodFormValues } from './types';

const formItemLayout = {
  style: { marginTop: 20 },
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 }
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 }
  }
};

interface Props {
  readonly form: WrappedFormUtils<AbsencePeriodFormValues>;
  readonly absencePeriod?: AbsencePeriod;
  readonly soonestAllowedAbsenceDate: Moment;
  readonly saving: boolean;
  readonly onSave: () => void;
}

const AbsenceFormComponent: React.FC<Props> = ({
  form,
  absencePeriod,
  soonestAllowedAbsenceDate,
  saving,
  onSave
}) => {
  const validateTime = (rule: any, value: string, callback: (message?: string) => void) => {
    const fromValue = form.getFieldValue('from') as Moment | undefined;
    const toValue = form.getFieldValue('to') as Moment | undefined;

    if (fromValue && toValue && toValue.isBefore(fromValue, 'day')) {
      callback('De einddag kan niet voor de startdag liggen');
    } else {
      callback();
    }
  };

  const disabledDate = (currentDate: Moment | null) => {
    if (currentDate === null) {
      return true;
    }

    return isBeforeAllowedAbsenceDate(currentDate, soonestAllowedAbsenceDate);
  };

  const fromMoment = absencePeriod ? moment(absencePeriod.from) : undefined;
  const toMoment = absencePeriod ? moment(absencePeriod.to) : undefined;

  const fromMomentDisabled =
    fromMoment && isBeforeAllowedAbsenceDate(fromMoment, soonestAllowedAbsenceDate);
  const toMomentDisabled =
    toMoment && isBeforeAllowedAbsenceDate(toMoment, soonestAllowedAbsenceDate);

  return (
    <Spin spinning={saving}>
      <Form {...formItemLayout} onSubmit={onSave}>
        <Form.Item label='Reden'>
          {form.getFieldDecorator('reason', {
            rules: [{ required: true, message: 'Kies een reden' }],
            initialValue: absencePeriod ? absencePeriod.reason : undefined
          })(
            <Select<AbsenceReason> placeholder='- Kies een reden -'>
              <Select.Option value='Holiday'>
                {getAbsenceReasonDisplayName('Holiday')}
              </Select.Option>
              <Select.Option value='Illness'>
                {getAbsenceReasonDisplayName('Illness')}
              </Select.Option>
              <Select.Option value='Other'>{getAbsenceReasonDisplayName('Other')}</Select.Option>
              <Select.Option value='Unknown'>
                {getAbsenceReasonDisplayName('Unknown')}
              </Select.Option>
            </Select>
          )}
        </Form.Item>

        <Form.Item label='Vanaf'>
          {form.getFieldDecorator('from', {
            rules: [{ required: true, message: 'Voer een dag in' }, { validator: validateTime }],
            initialValue: fromMoment
          })(
            <DatePicker
              format='DD-MM-YYYY'
              showToday={false}
              disabledDate={disabledDate}
              disabled={fromMomentDisabled}
            />
          )}
        </Form.Item>

        <Form.Item label='Tot en met'>
          {form.getFieldDecorator('to', {
            rules: [{ required: true, message: 'Voer een dag in' }, { validator: validateTime }],
            initialValue: toMoment
          })(
            <DatePicker
              format='DD-MM-YYYY'
              showToday={false}
              disabledDate={disabledDate}
              disabled={toMomentDisabled}
            />
          )}
        </Form.Item>
      </Form>
    </Spin>
  );
};

export { AbsenceFormComponent };
